import { useCallback, useEffect, useRef, useState } from "react";
import { Device } from "@twilio/voice-sdk";
import { NO_CALLER_ID_ASSIGNED } from "../data/callingMessages";
import PhoneCallsService from "../services/PhoneCallsService";

/**
 * Device errors that mean the credential itself is no longer usable. Anything
 * else -- a dropped signalling socket, a failed media handshake -- is transient
 * and must not take the dial pad away until the page is reloaded.
 *
 * @see https://www.twilio.com/docs/api/errors
 */
const FATAL_DEVICE_ERROR_CODES = [20101, 20103, 20104, 31204, 31205];

/**
 * "ringing" is an inbound call waiting to be answered; "dialing" is an outbound
 * call that has been placed but not yet picked up. The two need separate
 * controls (Answer/Decline versus Hang up), hence separate states.
 */
export type SoftphoneStatus =
  | "connecting"
  | "ready"
  | "dialing"
  | "ringing"
  | "on-call"
  | "unavailable";

/* eslint-disable no-unused-vars */
type TwilioCall = {
  on: (event: string, handler: (...args: never[]) => void) => unknown;
  accept: () => void;
  reject: () => void;
  disconnect: () => void;
  mute: (shouldMute: boolean) => void;
  parameters?: Record<string, string>;
};
/* eslint-enable no-unused-vars */

/**
 * Browser softphone lifecycle.
 *
 * The device registers under the rep's own client identity, so the inbound
 * hunt list can ring it, and dials out through the TwiML app, which asks the
 * backend for the TwiML in POST /api/v1/voice/outbound.
 */
export default function useSoftphone() {
  const [status, setStatus] = useState<SoftphoneStatus>("connecting");
  const [identity, setIdentity] = useState<string>("");
  // null means "registered, but with no number to call out from" -- an
  // unassigned rep still receives calls, so this is not an error state.
  const [callerId, setCallerId] = useState<string | null>(null);
  const [incomingFrom, setIncomingFrom] = useState<string>("");
  const [error, setError] = useState<string>("");
  const [muted, setMuted] = useState<boolean>(false);

  const deviceRef = useRef<Device | null>(null);
  const callRef = useRef<TwilioCall | null>(null);
  /**
   * True from the moment connect() is requested until it settles. callRef is
   * only set once connect() resolves, so without this a second dial in that
   * window would reach the SDK, which rejects it with "A Call is already
   * active" -- and React state is no help, because both dials may run before
   * the "dialing" status has re-rendered.
   */
  const connectingRef = useRef<boolean>(false);
  const registeredRef = useRef<boolean>(false);
  /**
   * Set once a token refresh fails. The current token dies within seconds, so
   * nothing the device reports after that -- an automatic re-registration, a
   * transient socket error -- may put the dial pad back or replace the
   * "reload the page" message.
   */
  const sessionExpiredRef = useRef<boolean>(false);

  const requestToken = useCallback(async () => {
    const response = await PhoneCallsService().token();

    if (response.code !== 200 || !response.data) {
      throw new Error(
        response.errors[0] || "A calling token could not be issued.",
      );
    }

    return response.data as {
      token: string;
      identity: string;
      caller_id?: string | null;
    };
  }, []);

  const attachCall = useCallback((call: TwilioCall) => {
    callRef.current = call;

    call.on("accept", () => {
      setStatus("on-call");
      setIncomingFrom("");
    });

    const release = () => {
      callRef.current = null;
      setMuted(false);
      setIncomingFrom("");
      setStatus("ready");
    };

    call.on("disconnect", release);
    call.on("cancel", release);
    call.on("reject", release);
    call.on("error", release);
  }, []);

  useEffect(() => {
    let cancelled = false;

    const boot = async () => {
      let credentials: {
        token: string;
        identity: string;
        caller_id?: string | null;
      };

      try {
        credentials = await requestToken();
      } catch (bootError) {
        if (cancelled) return;
        setError(
          bootError instanceof Error
            ? bootError.message
            : "Calling is offline.",
        );
        setStatus("unavailable");
        return;
      }

      if (cancelled) return;

      const device = new Device(credentials.token, { logLevel: "error" });
      deviceRef.current = device;
      setIdentity(credentials.identity);
      setCallerId(credentials.caller_id ?? null);

      device.on("registered", () => {
        if (sessionExpiredRef.current) return;

        registeredRef.current = true;
        setError("");
        setStatus("ready");
      });

      device.on("error", (deviceError: { code?: number; message?: string }) => {
        if (sessionExpiredRef.current) return;

        setError(deviceError?.message || "The phone connection dropped.");

        if (FATAL_DEVICE_ERROR_CODES.includes(Number(deviceError?.code))) {
          registeredRef.current = false;
          setStatus("unavailable");
          return;
        }

        // Transient: keep the softphone usable, but do not claim to be mid-call.
        setStatus((current) =>
          current === "on-call" ||
          current === "ringing" ||
          current === "dialing"
            ? current
            : (registeredRef.current && "ready") || current,
        );
      });

      device.on("incoming", (call: TwilioCall) => {
        attachCall(call);
        setIncomingFrom(call.parameters?.From || "");
        setStatus("ringing");
      });

      device.on("tokenWillExpire", async () => {
        try {
          const refreshed = await requestToken();
          device.updateToken(refreshed.token);
          // An admin assigning a number mid-shift takes effect here rather
          // than on the next page load.
          setCallerId(refreshed.caller_id ?? null);
        } catch {
          sessionExpiredRef.current = true;
          registeredRef.current = false;
          setError("The calling session expired. Reload the page.");
          setStatus("unavailable");
        }
      });

      try {
        await device.register();
      } catch (registerError) {
        // register() rejects -- sometimes with undefined rather than an Error --
        // whenever the device unregisters before it finishes registering. The
        // effect cleanup's destroy() does exactly that (StrictMode runs it on
        // every mount), so a torn-down hook must stay silent; a live one must
        // not sit on "Connecting..." forever.
        if (cancelled) return;
        setError(
          registerError instanceof Error && registerError.message
            ? registerError.message
            : "The phone could not connect.",
        );
        setStatus("unavailable");
      }
    };

    boot();

    return () => {
      cancelled = true;
      deviceRef.current?.destroy();
      deviceRef.current = null;
    };
  }, [attachCall, requestToken]);

  const startCall = useCallback(
    async (phoneNumber: string) => {
      const device = deviceRef.current;

      // One call at a time. The Device holds a single active call and every
      // control here drives callRef, so a second dial must never reach the SDK.
      if (
        !device ||
        status !== "ready" ||
        callRef.current ||
        connectingRef.current
      ) {
        return;
      }

      // Refused here rather than at each call site. The backend answers a rep
      // with no assigned number with NO_CALLER_ID_PROMPT and writes a failed
      // row, and there are three ways to reach this -- the customer list, the
      // call log's call-back buttons and the dial form -- so the guard belongs
      // where they all pass through.
      if (callerId === null) {
        setError(NO_CALLER_ID_ASSIGNED);
        return;
      }

      connectingRef.current = true;
      setError("");
      // The backend dials with answerOnBridge, so "accept" only fires once the
      // far end picks up. Until then the rep needs to see the call ringing and
      // be able to abort it, and every other dial button has to go away.
      setStatus("dialing");

      try {
        const call = (await device.connect({
          params: { To: phoneNumber },
        })) as unknown as TwilioCall;

        attachCall(call);
      } catch (dialError) {
        // A denied microphone or a rejected connect rejects here. Swallowing it
        // would clear the dial field and leave the rep with no idea why nothing
        // happened, and would surface as an unhandled rejection.
        setError(
          dialError instanceof Error
            ? dialError.message
            : "The call could not be placed.",
        );

        // Only fall back to ready when there is no live call to fall back
        // from. If the SDK refused because a call is already active, that
        // call still owns callRef and the controls must keep driving it.
        if (!callRef.current) {
          setStatus("ready");
        }
      } finally {
        connectingRef.current = false;
      }
    },
    [attachCall, callerId, status],
  );

  // A live call dies with the page: navigating away unmounts the hook, the
  // cleanup destroys the Device and the customer is hung up on mid-sentence.
  // show_customer1.php is a table of links that all leave the page, so the rep
  // needs to be asked first. Only while a call is actually up -- a prompt on
  // every idle navigation would train them to click straight through it.
  useEffect(() => {
    const callIsLive =
      status === "dialing" || status === "ringing" || status === "on-call";

    if (!callIsLive) return undefined;

    const warnBeforeUnload = (event: BeforeUnloadEvent) => {
      // Browsers show their own wording and ignore a custom string; both of
      // these are needed to trigger the prompt across engines.
      event.preventDefault();
      // eslint-disable-next-line no-param-reassign
      event.returnValue = "";
    };

    window.addEventListener("beforeunload", warnBeforeUnload);

    return () => window.removeEventListener("beforeunload", warnBeforeUnload);
  }, [status]);

  const acceptCall = useCallback(() => {
    callRef.current?.accept();
    setStatus("on-call");
    setIncomingFrom("");
  }, []);

  const rejectCall = useCallback(() => {
    callRef.current?.reject();
    callRef.current = null;
    setIncomingFrom("");
    setStatus("ready");
  }, []);

  const hangUp = useCallback(() => {
    callRef.current?.disconnect();
  }, []);

  const toggleMute = useCallback(() => {
    setMuted((wasMuted) => {
      const nextMuted = !wasMuted;
      callRef.current?.mute(nextMuted);
      return nextMuted;
    });
  }, []);

  return {
    status,
    identity,
    callerId,
    // A capability, not a moment: true whenever the rep has a number to call
    // out from, whatever the device is doing right now.
    canPlaceCalls: callerId !== null,
    incomingFrom,
    error,
    muted,
    startCall,
    acceptCall,
    rejectCall,
    hangUp,
    toggleMute,
  };
}
